import { z } from "zod"

const Weight = z.union([
  z.literal("100"),
  z.literal("200"),
  z.literal("300"),
  z.literal("400"),
  z.literal("500"),
  z.literal("600"),
  z.literal("700"),
  z.literal("800"),
  z.literal("900"),
])

const Variant = z.object({
  weight: Weight,
  src: z.string().min(1),
  style: z.union([z.literal("normal"), z.literal("italic")]),
  format: z.union([z.literal("ttf"), z.literal("otf"), z.literal("woff"), z.literal("woff2")]),
})

export type TVariant = z.infer<typeof Variant>

const FontFamily = z.object({
  name: z.string().min(1),
  variants: z.array(Variant),
})

export const Fonts = z.array(FontFamily)
export type TFonts = z.infer<typeof Fonts>

// Version 1 ---------------------
export const PageSettingsV1 = z.object({
  // General
  font: z.string().optional(),
  googleFonts: z.string().optional(),
  headingFont: z.string().optional(),
  fontSize: z.string().optional(),
  spacing: z.string().optional(),
  statusBarPadding: z.string().optional(),
  paywallBackgroundColor: z.string().optional(),
  paywallMaxWidth: z.string().optional(),
  pjs: z.string().optional(),
  contentAlignment: z
    .union([z.literal("top"), z.literal("bottom"), z.literal("center")])
    .optional(),
  contentPadding: z.string().optional(),
  autoAdjustContentAlignment: z.union([z.literal("true"), z.literal("false")]).optional(),
  insetsTopContent: z.union([z.literal("true"), z.literal("false")]).optional(),
  sectionPaddingX: z.string().optional(),
  sectionPaddingY: z.string().optional(),
  brandColor: z.string().optional(),
  brandTextColor: z.string().optional(),
  foregroundColor: z.string().optional(),
  previewDarkMode: z.union([z.literal("true"), z.literal("false")]).optional(),

  // Custom code injection
  customCSS: z.string().optional(),
  customFonts: Fonts.optional(),
  customHeadingFont: z.string().optional(),
  customBodyFont: z.string().optional(),
  useCustomFonts: z.union([z.literal("true"), z.literal("false")]).optional(),
  customJS: z.string().optional(),

  // Navbar
  minNavbarHeight: z.string().optional(),
  nav: z.union([z.literal("show"), z.literal("hide")]).optional(), // hide, show
  navBackgroundBlur: z.union([z.literal("true"), z.literal("false")]).optional(),
  navBackgroundColor: z.string().optional(),
  navButtonIconsSize: z.string().optional(),
  navCenter: z
    .union([
      z.literal("show"),
      z.literal("hide"),
      z.literal("mixed"),
      z.literal("text"),
      z.literal("image"),
    ])
    .optional(),
  navLeft: z
    .union([
      z.literal("hide"),
      z.literal("mixed"),
      z.literal("text"),
      z.literal("icon"),
      z.literal("doubleIcon"),
      z.literal("doubleText"),
      z.literal("all"),
    ])
    .optional(),
  navLeftIconURL: z.string().optional(),
  navLeftInnerIconURL: z.string().optional(),
  navPosition: z.union([z.literal("fixed"), z.literal("inline")]).optional(),
  navRight: z
    .union([
      z.literal("hide"),
      z.literal("mixed"),
      z.literal("text"),
      z.literal("icon"),
      z.literal("doubleIcon"),
      z.literal("doubleText"),
      z.literal("all"),
    ])
    .optional(),
  navRightIconURL: z.string().optional(),
  navRightInnerIconURL: z.string().optional(),
  logoHeight: z.string().optional(),
  logoURL: z.string().optional(),

  // Product Drawer
  backgroundColor: z.string().optional(),
  direction: z.union([z.literal("vertical"), z.literal("horizontal")]).optional(),
  reverseDirection: z.union([z.literal("true"), z.literal("false")]).optional(),
  containerRadius: z.string().optional(),
  hideProducts: z.union([z.literal("true"), z.literal("false"), z.literal("auto")]).optional(),
  productBackgroundColor: z.string().optional(),
  productCount: z.union([z.literal("one"), z.literal("two"), z.literal("three")]).optional(),
  productDescription: z.union([z.literal("show"), z.literal("hide")]).optional(),
  productDivider: z.union([z.literal("show"), z.literal("hide")]).optional(),
  productPadding: z.string().optional(),
  productRadius: z.string().optional(),
  purchaseRadius: z.string().optional(),
  primaryProductBadge: z.union([z.literal("show"), z.literal("hide")]).optional(),
  secondaryProductBadge: z.union([z.literal("show"), z.literal("hide")]).optional(),
  tertiaryProductBadge: z.union([z.literal("show"), z.literal("hide")]).optional(),
  borderColor: z.string().optional(),
  borderWidth: z.string().optional(),
  selectedBorderWidth: z.string().optional(),
  badgeRadius: z.string().optional(),
  buttonBackgroundColor: z.string().optional(),

  // Footer
  footer: z
    .union([z.literal("normal"), z.literal("compact"), z.literal("minimal"), z.literal("hide")])
    .optional(),
  footerAlignment: z.union([z.literal("spread"), z.literal("center")]).optional(),
  footerDivider: z.union([z.literal("show"), z.literal("hide")]).optional(),
  footerPosition: z.union([z.literal("fixed"), z.literal("inline")]).optional(),

  // Unused
  forceDarkMode: z.union([z.literal("true"), z.literal("false")]).optional(),
  brandSecondaryColor: z.string().optional(),
  cardBackgroundBlur: z.union([z.literal("true"), z.literal("false")]).optional(),
  cardPadding: z.string().optional(),
})

export type TPageSettingsV1 = z.infer<typeof PageSettingsV1>
export type TPageSettingsKeyesV1 = keyof TPageSettingsV1

// Version 2 ---------------------
export const PageSettingsV2 = PageSettingsV1.extend({
  showProductCheckMark: z.union([z.literal("true"), z.literal("false")]).optional(),
})

export type TPageSettingsV2 = z.infer<typeof PageSettingsV2>
export type TPageSettingsKeyesV2 = keyof TPageSettingsV2

// Version 3 ---------------------
export const PageSettingsV3 = PageSettingsV2.extend({
  showProductCheckMark: z.union([z.literal("show"), z.literal("hide")]).optional(),
  selectedCheckBoxImage: z.string().optional(),
  unselectedCheckBoxImage: z.string().optional(),
  checkMarkMargin: z.string().optional(),
  linkSelectedProduct: z.union([z.literal("true"), z.literal("false")]).optional(),
})

export type TPageSettingsV3 = z.infer<typeof PageSettingsV3>
export type TPageSettingsKeyesV3 = keyof TPageSettingsV3

// Version 4 ---------------------
export const PageSettingsV4 = PageSettingsV3.extend({
  purchaseButtonLineTwo: z.union([z.literal("show"), z.literal("hide")]).optional(),
})

export type TPageSettingsV4 = z.infer<typeof PageSettingsV4>
export type TPageSettingsKeyesV4 = keyof TPageSettingsV4

// Version 5 ---------------------
export const PageSettingsV5 = PageSettingsV4.extend({
  hideFixedPurchaseDrawer: z.union([z.literal("true"), z.literal("false")]).optional(),
})

export type TPageSettingsV5 = z.infer<typeof PageSettingsV5>
export type TPageSettingsKeyesV5 = keyof TPageSettingsV5

// Version 6 ---------------------
export const PageSettingsV6 = PageSettingsV5.extend({
  backgroundContentMaxWidth: z.union([z.literal("100vw"), z.literal("paywallMaxWidth")]).optional(),
})

export type TPageSettingsV6 = z.infer<typeof PageSettingsV6>
export type TPageSettingsKeyesV6 = keyof TPageSettingsV6

// Latest version ----------------
export const PageSettings = PageSettingsV6
export type TPageSettings = TPageSettingsV6
export type TPageSettingsKeyes = TPageSettingsKeyesV6
